import React, { useContext, useRef, useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { IconButton, Snackbar, SnackbarContent } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { AiOutlineCheckCircle, AiOutlineSend } from 'react-icons/ai';
import { FaFacebook, FaGithub, FaInstagram, FaLinkedinIn, FaMediumM, FaStackOverflow, FaTwitter, FaWhatsapp, FaYoutube } from 'react-icons/fa';
import { FiAtSign, FiPhone } from 'react-icons/fi';
import { HiOutlineLocationMarker } from 'react-icons/hi';
import isEmail from 'validator/lib/isEmail';

import { ThemeContext } from '../../contexts/ThemeContext';
import { contactsData } from '../../data/contactsData';
import { socialsData } from '../../data/socialsData';
import './Contacts.css';

const useStyles = makeStyles(() => ({
    input: {
        border: ({ theme }) => `2px solid ${theme.buttonColor}`,
        backgroundColor: ({ theme }) => `${theme.secondary}`,
        color: ({ theme }) => `${theme.tertiary}`,
        fontFamily: 'var(--primaryFont)',
        fontWeight: 500,
        transition: 'border 0.2s ease-in-out',
        '&:focus': { border: ({ theme }) => `2px solid ${theme.primary}` },
    },
    message: {
        border: ({ theme }) => `2px solid ${theme.buttonColor}`,
        backgroundColor: ({ theme }) => `${theme.secondary}`,
        color: ({ theme }) => `${theme.tertiary}`,
        fontFamily: 'var(--primaryFont)',
        fontWeight: 500,
        transition: 'border 0.2s ease-in-out',
        '&:focus': { border: ({ theme }) => `2px solid ${theme.primary}` },
    },
    label: {
        backgroundColor: ({ theme }) => `${theme.secondary}`,
        color: ({ theme }) => `${theme.tertiary}`,
        fontFamily: 'var(--primaryFont)',
        fontWeight: 600,
        fontSize: '0.9rem',
        padding: '0 5px',
        transform: 'translate(25px,50%)',
        display: 'inline-flex',
    },
    socialIcon: {
        width: '45px',
        height: '45px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '21px',
        backgroundColor: ({ theme }) => theme.buttonColor,
        color: ({ theme }) => theme.secondary,
        transition: '250ms ease-in-out',
        '&:hover': {
            transform: 'scale(1.1)',
            color: ({ theme }) => theme.secondary,
            backgroundColor: ({ theme }) => theme.primary,
        },
    },
    detailsIcon: {
        backgroundColor: ({ theme }) => theme.buttonColor,
        color: ({ theme }) => theme.secondary,
        borderRadius: '50%',
        width: '45px',
        height: '45px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '23px',
        transition: '250ms ease-in-out',
        flexShrink: 0,
        '&:hover': {
            transform: 'scale(1.1)',
            color: ({ theme }) => theme.secondary,
            backgroundColor: ({ theme }) => theme.primary,
        },
    },
    submitBtn: {
        backgroundColor: ({ theme }) => theme.primary,
        color: ({ theme }) => theme.secondary,
        transition: '250ms ease-in-out',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        overflow: 'hidden',
        position: 'relative',
        '&:hover:not(:disabled)': {
            transform: 'scale(1.08)',
            color: ({ theme }) => theme.secondary,
            backgroundColor: ({ theme }) => theme.buttonColor,
        },
    },
}));

function Contacts() {
    const { theme } = useContext(ThemeContext);
    const classes = useStyles({ theme });

    const [open, setOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [success, setSuccess] = useState(false);
    const [errMsg, setErrMsg] = useState('');
    
    const [location, setLocation] = useState('');
    const [userIp, setUserIp] = useState('');
    const [userIsp, setUserIsp] = useState('');
    const subject = 'General Portfolio Inquiry 💼';

    const form = useRef();

    useEffect(() => {
        fetch('https://ipapi.co/json/')
            .then(res => res.json())
            .then(data => {
                if (data.city && data.country_name) {
                    setLocation(`${data.city}, ${data.country_name}`);
                    setUserIp(data.ip || '');
                    setUserIsp(data.org || '');
                }
            })
            .catch(() => setLocation('Unknown Location'));
    }, []);

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setOpen(false);
    };

    const handleContactForm = async (e) => {
        e.preventDefault();

        if (!name || !email || !message) {
            setErrMsg('Enter all the fields');
            setOpen(true);
            return;
        }

        if (!isEmail(email)) {
            setErrMsg('Invalid email');
            setOpen(true);
            return;
        }

        setSending(true);
        setSuccess(false); // নতুন সাবমিশনে সাকসেস ফলস থাকবে

        try {
            const result = await emailjs.sendForm('service_0ufj0tr', 'template_hq8zmp8', form.current, 'afeU9_MAjsMDpy3l5');
            console.log('success');
            
            setSuccess(true); // ৩ নম্বর ছবির মতো টিক চিহ্ন দেখাবে
            setErrMsg('Message Sent Successfully!');
            setName('');
            setEmail('');
            setMessage('');
            setOpen(true);

            if (result.status === 200) {
                e.target.reset();
            }

            // ফিক্স: ৩ সেকেন্ড পর বাটনটি আবার ১ম ছবির মতো স্বাভাবিক হয়ে যাবে
            setTimeout(() => {
                setSuccess(false);
            }, 3000);

        } catch (error) {
            console.log(error.text || error);
            setErrMsg('Failed to send message.');
            setOpen(true);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className='contacts' id='contacts' style={{ backgroundColor: theme.secondary }}>
            <div className='contacts--container'>
                <h1 style={{ color: theme.primary }}>Contacts</h1>
                <div className='contacts-body'>
                    <div className='contacts-form'>
                        <form ref={form} onSubmit={handleContactForm}>
                            <div className='input-container'>
                                <label htmlFor='Name' className={classes.label}>Name</label>
                                <input
                                    placeholder='Your Name'
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    type='text'
                                    name='user_name'
                                    className={`form-input ${classes.input}`}
                                />
                            </div>
                            <div className='input-container'>
                                <label htmlFor='Email' className={classes.label}>Email</label>
                                <input
                                    placeholder='example@example.com'
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    type='email'
                                    name='user_email'
                                    className={`form-input ${classes.input}`}
                                />
                            </div>
                            <div className='input-container'>
                                <label htmlFor='Message' className={classes.label}>Message</label>
                                <textarea
                                    placeholder='Type your message....'
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    name='message'
                                    className={`form-message ${classes.message}`}
                                />
                            </div>

                            {/* Hidden Fields */}
                            <input type="hidden" name="submission_time" value={new Date().toLocaleString('en-US', { timeZone: 'Asia/Dhaka' })} />
                            <input type="hidden" name="referral_source" value={document.referrer || "Direct Visit / Bookmarked"} />
                            <input type="hidden" name="subject_topic" value={subject} />
                            <input type="hidden" name="user_location" value={location} />
                            <input type="hidden" name="user_ip" value={userIp} />
                            <input type="hidden" name="user_isp" value={userIsp} />

                            <div className='submit-btn'>
                                <button disabled={sending} type='submit' className={classes.submitBtn} style={{ padding: '0 30px', height: '55px' }}>
                                    {/* ২য় ছবির মতো হলে Sending লেখা থাকবে, সফল হলে Sent এবং এমনিতে Send */}
                                    <p>{sending ? 'Sending...' : success ? 'Sent' : 'Send'}</p>
                                    <div className='submit-icon' style={{ display: 'flex', alignItems: 'center', width: '24px', height: '24px', justifyContent: 'center' }}>
                                        <AiOutlineSend
                                            className='send-icon'
                                            style={{
                                                animation: sending ? 'fly 2s infinite linear' : 'none',
                                                display: success ? 'none' : 'block',
                                            }}
                                        />
                                        <AiOutlineCheckCircle
                                            className='success-icon'
                                            style={{
                                                display: success ? 'inline-flex' : 'none',
                                                fontSize: '1.2rem'
                                            }}
                                        />
                                    </div>
                                </button>
                            </div>
                        </form>
                        
                        <Snackbar
                            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                            open={open}
                            autoHideDuration={4000}
                            onClose={handleClose}
                        >
                            <SnackbarContent
                                action={
                                    <IconButton size='small' aria-label='close' color='inherit' onClick={handleClose}>
                                        <CloseIcon fontSize='small' />
                                    </IconButton>
                                }
                                style={{ backgroundColor: theme.primary, color: theme.secondary, fontFamily: 'var(--primaryFont)' }}
                                message={errMsg}
                            />
                        </Snackbar>
                    </div>

                    <div className='contacts-details'>
                        <a href={`mailto:${contactsData.email}`} className='personal-details'>
                            <div className={classes.detailsIcon}><FiAtSign /></div>
                            <p style={{ color: theme.tertiary }}>{contactsData.email}</p>
                        </a>
                        <a href={`tel:${contactsData.phoneN}`} className='personal-details'>
                            <div className={classes.detailsIcon}><FiPhone /></div>
                            <p style={{ color: theme.tertiary }}>{contactsData.phone}</p>
                        </a>
                        <a href={`tel:${contactsData.phoneNB}`} className='personal-details'>
                            <div className={classes.detailsIcon}><FiPhone /></div>
                            <p style={{ color: theme.tertiary }}>{contactsData.phoneB}</p>
                        </a>
                        <a href={contactsData.whatsapp} className='personal-details' target="_blank" rel="noreferrer">
                            <div className={classes.detailsIcon}><FaWhatsapp /></div>
                            <p style={{ color: theme.tertiary }}>{contactsData.phoneWp}</p>
                        </a>
                        <a href={contactsData.map} className='personal-details' target="_blank" rel="noreferrer">
                            <div className={classes.detailsIcon}><HiOutlineLocationMarker /></div>
                            <p style={{ color: theme.tertiary }}>{contactsData.address}</p>
                        </a>

                        <div className='socialmedia-icons'>
                            {socialsData.twitter && <a href={socialsData.twitter} target='_blank' rel='noreferrer' className={classes.socialIcon}><FaTwitter aria-label='Twitter' /></a>}
                            {socialsData.github && <a href={socialsData.github} target='_blank' rel='noreferrer' className={classes.socialIcon}><FaGithub aria-label='GitHub' /></a>}
                            {socialsData.linkedIn && <a href={socialsData.linkedIn} target='_blank' rel='noreferrer' className={classes.socialIcon}><FaLinkedinIn aria-label='LinkedIn' /></a>}
                            {socialsData.medium && <a href={socialsData.medium} target='_blank' rel='noreferrer' className={classes.socialIcon}><FaMediumM aria-label='Medium' /></a>}
                            {socialsData.youtube && <a href={socialsData.youtube} target='_blank' rel='noreferrer' className={classes.socialIcon}><FaYoutube aria-label='YouTube' /></a>}
                            {socialsData.stackOverflow && <a href={socialsData.stackOverflow} target='_blank' rel='noreferrer' className={classes.socialIcon}><FaStackOverflow aria-label='Stack Overflow' /></a>}
                            {socialsData.instagram && <a href={socialsData.instagram} target='_blank' rel='noreferrer' className={classes.socialIcon}><FaInstagram aria-label='Instagram' /></a>}
                            {socialsData.facebook && <a href={socialsData.facebook} target='_blank' rel='noreferrer' className={classes.socialIcon}><FaFacebook aria-label='facebook' /></a>}
                        </div>
                    </div>
                </div>
            </div>
            <img src={theme.contactsimg} alt='contacts' className='contacts--img' />
        </div>
    );
}

export default Contacts;